import React from "react";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import AppPreloader from "../../components/preloader/AppPreloader";
import {
  ACTIVATE_PERSON,
  ADD_PERSON_MUTATION,
  GET_PERSON,
  activatePerson,
  addPerson,
} from "./PersonAPI";
import { useZorni } from "../useZorni";
import { useQuery, useMutation } from "@apollo/client";
import { useOperation } from "../../../dashboard/hooks/useOperation";

export const PersonContext = createContext();

export const usePerson = () => {
  const context = useContext(PersonContext);
  if (!context) {
    throw new Error("usePerson must be used within a Provider");
  }
  return context;
};

export const PersonProvider = ({ children }) => {
  const {
    userId,
    firstConnection,
    email,
    type,
    isOwner,
    accessToken,
    isAuthenticated,
    triggerUserCreation,
  } = useZorni();

  const [addPersonMutation] = useMutation(ADD_PERSON_MUTATION);
  const [activatePersonMutation] = useMutation(ACTIVATE_PERSON);
  const { loading, error, data } = useQuery(GET_PERSON, {
    variables: { personId: userId },
    skip: !isAuthenticated || firstConnection || !userId, // Empêche l'appel si firstConnection est true ou il n'est pas connecté
  });

  const [personInfo, setPersonInfo] = useState(null);
  const hasCreatedPerson = useRef(false); // Empêche la recréation de l'utilisateur
  const { showToast } = useOperation();

  useEffect(() => {
    const fetchData = async () => {
      if (!personInfo && data?.QR_GET_PERSON) {
        setPersonInfo(data.QR_GET_PERSON.data);
      }
    };

    const handleAddPerson = async () => {
      if (!isAuthenticated || !firstConnection) return;
      if (hasCreatedPerson.current) return; // Empêche l'appel multiple

      try {
        const shouldActivate =
          isAuthenticated && firstConnection && type === "PRO" && !isOwner;

        if (shouldActivate) {
          const { data } = await activatePerson(
            activatePersonMutation,
            userId,
            accessToken
          );
          if (loading) {
            return;
          }
          if (data?.MT_ACTIVATE_PERSON?.data?.id) {
            hasCreatedPerson.current = true;
            triggerUserCreation();
            showToast("Utilisateur activé avec succès");
          } else {
            showToast("Une erreur est survenue", "error");
          }
          return;
        }

        const personInput = {
          id: userId,
          email: email,
          type: type,
          operation: "CREATION",
        };

        const { data } = await addPerson(
          addPersonMutation,
          personInput,
          accessToken
        );
        if (loading) {
          return;
        }
        if (data?.MT_ADD_PERSON) {
          hasCreatedPerson.current = true; // Marque comme déjà créé
          triggerUserCreation(); // Met `firstConnection` à `false`
          showToast("Utilisateur créé avec succès");
        } else {
          showToast("Une erreur est survenue", "error");
        }
      } catch (error) {
        showToast("Une erreur est survenue", "error");
        console.error("Error adding person:", error);
      }
    };

    if (isAuthenticated) {
      if (firstConnection) {
        handleAddPerson();
      } else {
        fetchData();
      }
    }
  }, [
    isAuthenticated,
    firstConnection,
    loading,
    error,
    data,
    personInfo,
    userId,
    email,
    type,
    isOwner,
    accessToken,
    triggerUserCreation,
    activatePersonMutation,
    addPersonMutation,
    showToast,
  ]);

  return (
    <PersonContext.Provider value={{ personInfo, setPersonInfo }}>
      {loading ? <AppPreloader /> : children}
    </PersonContext.Provider>
  );
};
